import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { XIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, ShieldIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
export const TeacherSignUpLayout = () => {
  const navigate = useNavigate();
  const {
    registerTeacher,
    currentUser
  } = useAuth();
  const US_STATES = [{
    abbr: 'AL',
    name: 'Alabama'
  }, {
    abbr: 'AK',
    name: 'Alaska'
  }, {
    abbr: 'AZ',
    name: 'Arizona'
  }, {
    abbr: 'AR',
    name: 'Arkansas'
  }, {
    abbr: 'CA',
    name: 'California'
  }, {
    abbr: 'CO',
    name: 'Colorado'
  }, {
    abbr: 'CT',
    name: 'Connecticut'
  }, {
    abbr: 'DE',
    name: 'Delaware'
  }, {
    abbr: 'FL',
    name: 'Florida'
  }, {
    abbr: 'GA',
    name: 'Georgia'
  }, {
    abbr: 'HI',
    name: 'Hawaii'
  }, {
    abbr: 'ID',
    name: 'Idaho'
  }, {
    abbr: 'IL',
    name: 'Illinois'
  }, {
    abbr: 'IN',
    name: 'Indiana'
  }, {
    abbr: 'IA',
    name: 'Iowa'
  }, {
    abbr: 'KS',
    name: 'Kansas'
  }, {
    abbr: 'KY',
    name: 'Kentucky'
  }, {
    abbr: 'LA',
    name: 'Louisiana'
  }, {
    abbr: 'ME',
    name: 'Maine'
  }, {
    abbr: 'MD',
    name: 'Maryland'
  }, {
    abbr: 'MA',
    name: 'Massachusetts'
  }, {
    abbr: 'MI',
    name: 'Michigan'
  }, {
    abbr: 'MN',
    name: 'Minnesota'
  }, {
    abbr: 'MS',
    name: 'Mississippi'
  }, {
    abbr: 'MO',
    name: 'Missouri'
  }, {
    abbr: 'MT',
    name: 'Montana'
  }, {
    abbr: 'NE',
    name: 'Nebraska'
  }, {
    abbr: 'NV',
    name: 'Nevada'
  }, {
    abbr: 'NH',
    name: 'New Hampshire'
  }, {
    abbr: 'NJ',
    name: 'New Jersey'
  }, {
    abbr: 'NM',
    name: 'New Mexico'
  }, {
    abbr: 'NY',
    name: 'New York'
  }, {
    abbr: 'NC',
    name: 'North Carolina'
  }, {
    abbr: 'ND',
    name: 'North Dakota'
  }, {
    abbr: 'OH',
    name: 'Ohio'
  }, {
    abbr: 'OK',
    name: 'Oklahoma'
  }, {
    abbr: 'OR',
    name: 'Oregon'
  }, {
    abbr: 'PA',
    name: 'Pennsylvania'
  }, {
    abbr: 'RI',
    name: 'Rhode Island'
  }, {
    abbr: 'SC',
    name: 'South Carolina'
  }, {
    abbr: 'SD',
    name: 'South Dakota'
  }, {
    abbr: 'TN',
    name: 'Tennessee'
  }, {
    abbr: 'TX',
    name: 'Texas'
  }, {
    abbr: 'UT',
    name: 'Utah'
  }, {
    abbr: 'VT',
    name: 'Vermont'
  }, {
    abbr: 'VA',
    name: 'Virginia'
  }, {
    abbr: 'WA',
    name: 'Washington'
  }, {
    abbr: 'WV',
    name: 'West Virginia'
  }, {
    abbr: 'WI',
    name: 'Wisconsin'
  }, {
    abbr: 'WY',
    name: 'Wyoming'
  }];
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    city: '',
    state: ''
  });
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const [showFirebaseRulesHelp, setShowFirebaseRulesHelp] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  useEffect(() => {
    if (currentUser) {
      if (currentUser.emailVerified) {
        navigate('/teacher-dashboard');
      } else {
        navigate('/teacher-email-verification');
      }
    }
  }, [currentUser, navigate]);
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);
  useEffect(() => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [formData.password, formData.confirmPassword]);
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      id,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    setErrorMessage('');
  };
  useEffect(() => {
    if (!signupComplete) return;
    const countdownInterval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          console.log('Redirecting to email verification page...');
          navigate('/teacher-email-verification');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [signupComplete, navigate]);
  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setErrorMessage('Please enter your first name');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return false;
    }
    if (!formData.city.trim()) {
      setErrorMessage('Please enter your city');
      return false;
    }
    if (!formData.state) {
      setErrorMessage('Please select your state');
      return false;
    }
    if (!verifyChecked) {
      setErrorMessage('Please verify the accuracy of your information');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registrationAttempted || isSubmitting) {
      console.log('[Signup] Registration already in progress or completed');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setShowFirebaseRulesHelp(false);
    if (!validateForm()) return;
    setIsSubmitting(true);
    setRegistrationAttempted(true);
    try {
      console.log('[Signup] Starting teacher registration process...');
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      const city = formData.city.trim();
      const state = formData.state;
      const location = `${city}, ${state}`;
      const result = await registerTeacher(formData.email.trim(), formData.password, formData.firstName.trim(), formData.lastName.trim(), location, city, state);
      console.log('[Signup] Teacher registration successful!', result);
      setIsSubmitting(false);
      setSuccessMessage('Your teacher account has been created! A verification email has been sent to your inbox.');
      setSignupComplete(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        city: '',
        state: ''
      });
      setVerifyChecked(false);
      navigate('/teacher-email-verification');
    } catch (error: any) {
      console.error('[Signup] Signup error:', error);
      setRegistrationAttempted(false);
      if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password is too weak. Please use a stronger password.');
      } else if (error.message && error.message.includes('Firebase permissions error')) {
        setErrorMessage('Missing or insufficient permissions.');
        setShowFirebaseRulesHelp(true);
      } else {
        setErrorMessage(error.message || 'An error occurred. Please try again later.');
      }
      setIsSubmitting(false);
    }
  };
  return <div className="w-full" data-id="element-12197">
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden" data-id="element-12198">
        <div className="absolute inset-0 z-0" data-id="element-12199">
          <div className="absolute inset-0 bg-navy-900/70 z-10" data-id="element-12200"></div>
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('https://uploadthingy.s3.us-west-1.amazonaws.com/s8ktVYbcqPGNW3DKAXWQDj/Piano_teacher_3.jpg')`
        }} data-id="element-12201"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-12202">
          <div className="text-center" data-id="element-12203">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6" data-id="element-12204">
              Start Teaching on{' '}
              <span className="text-deep-pink" data-id="element-12205">MaestroConnect</span>
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto" data-id="element-12206">
              Join a growing network of qualified music teachers and connect
              with students in your area or online.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-navy-900/5" data-id="element-12207">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-12208">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-12209">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-6 text-center" data-id="element-12210">
              Create Your Teacher Profile
            </h2>

            {successMessage && <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start" data-id="element-12211">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-12212" />
                <div data-id="element-12213">
                  <span data-id="element-12214">{successMessage}</span>
                  {signupComplete && <p className="text-sm mt-1" data-id="element-12215">
                      Redirecting to complete your profile in{' '}
                      {redirectCountdown} seconds...
                    </p>}
                </div>
              </div>}

            {errorMessage && <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start" data-id="element-12216">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-12217" />
                <span data-id="element-12218">{errorMessage}</span>
              </div>}

            {showFirebaseRulesHelp && <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg" data-id="element-12219">
                <h3 className="font-medium mb-2" data-id="element-12220">
                  Firebase Security Rules Issue
                </h3>
                <p className="mb-2" data-id="element-12221">
                  Your Firebase security rules need to be updated to allow
                  document creation. Update your Firestore rules to:
                </p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-2" data-id="element-12222">
                  {`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /users/{userId} {
      allow read: if request.auth != null && request.auth.uid == userId;
      allow create: if request.auth != null && request.auth.uid == userId;
      allow update: if request.auth != null && request.auth.uid == userId;
    }
    match /teachers/{teacherId} {
      allow read: if request.auth != null && request.auth.uid == teacherId;
      allow create: if request.auth != null && request.auth.uid == teacherId;
      allow update: if request.auth != null && request.auth.uid == teacherId;
      match /calendar/{eventId} {
        allow read, write: if request.auth != null && request.auth.uid == teacherId;
      }
    }
    match /students/{studentId} {
      allow read: if request.auth != null && request.auth.uid == studentId;
      allow create: if request.auth != null && request.auth.uid == studentId;
      allow update: if request.auth != null && request.auth.uid == studentId;
    }
  }
}`}
                </pre>
                <p className="text-sm" data-id="element-12223">
                  Please update these rules in your Firebase Console under
                  Firestore Database &gt; Rules.
                </p>
              </div>}

            {!signupComplete && <form onSubmit={handleSubmit} data-id="element-12224">
                <div className="space-y-6" data-id="element-12225">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12226">
                    <div data-id="element-12227">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12228">
                        First Name <span className="text-red-500" data-id="element-12229">*</span>
                      </label>
                      <input type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Your first name" required disabled={isSubmitting} data-id="element-12230" />
                    </div>
                    <div data-id="element-12231">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12232">
                        Last Name
                      </label>
                      <input type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Your last name" disabled={isSubmitting} data-id="element-12233" />
                    </div>
                  </div>

                  <div data-id="element-12234">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12235">
                      Email Address <span className="text-red-500" data-id="element-12236">*</span>
                    </label>
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isSubmitting} data-id="element-12237" />
                  </div>

                  <div data-id="element-12238">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12239">
                      Password <span className="text-red-500" data-id="element-12240">*</span>
                    </label>
                    <div className="relative" data-id="element-12241">
                      <input type={passwordVisible ? 'text' : 'password'} id="password" value={formData.password} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Create a secure password (min. 8 characters)" required disabled={isSubmitting} data-id="element-12242" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setPasswordVisible(!passwordVisible)} disabled={isSubmitting} data-id="element-12243">
                        {passwordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-12244" /> : <EyeIcon className="h-5 w-5" data-id="element-12245" />}
                      </button>
                    </div>
                    <div className="mt-2" data-id="element-12246">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-12247">
                        <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-12248"></div>
                      </div>
                      <div className="mt-1 flex justify-between items-center" data-id="element-12249">
                        <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-12250">
                          {passwordStrength.label}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-12251">
                          Use 8+ characters, include a number and symbol
                        </span>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12252">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12253">
                      Confirm Password <span className="text-red-500" data-id="element-12254">*</span>
                    </label>
                    <div className="relative" data-id="element-12255">
                      <input type={confirmPasswordVisible ? 'text' : 'password'} id="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900 ${passwordError ? 'border-red-500' : 'border-gray-300'}`} placeholder="Confirm your password" required disabled={isSubmitting} data-id="element-12256" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isSubmitting} data-id="element-12257">
                        {confirmPasswordVisible ? <EyeOffIcon className="h-5 w-5" data-id="element-12258" /> : <EyeIcon className="h-5 w-5" data-id="element-12259" />}
                      </button>
                    </div>
                    {passwordError && <p className="mt-1 text-sm text-red-500" data-id="element-12260">
                        {passwordError}
                      </p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12261">
                    <div data-id="element-12262">
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12263">
                        City <span className="text-red-500" data-id="element-12264">*</span>
                      </label>
                      <input type="text" id="city" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="City" required disabled={isSubmitting} data-id="element-12265" />
                    </div>
                    <div data-id="element-12266">
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12267">
                        State <span className="text-red-500" data-id="element-12268">*</span>
                      </label>
                      <select id="state" value={formData.state} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" required disabled={isSubmitting} data-id="element-12269">
                        <option value="" data-id="element-12270">Select a state</option>
                        {US_STATES.map(s => <option key={s.abbr} value={s.abbr} data-id="element-12271">
                            {s.name}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-12272">
                    <div className="flex items-center h-5" data-id="element-12273">
                      <input id="verify" name="verify" type="checkbox" checked={verifyChecked} onChange={() => setVerifyChecked(!verifyChecked)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" required disabled={isSubmitting} data-id="element-12274" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-12275">
                      <label htmlFor="verify" className="font-medium text-gray-700" data-id="element-12276">
                        I verify the accuracy of my information and agree to the{' '}
                        <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-700" data-id="element-12277">
                          Terms of Service
                        </Link>
                      </label>
                    </div>
                  </div>

                  <button type="submit" disabled={isSubmitting || !!passwordError || registrationAttempted} className={`w-full ${isSubmitting || passwordError || registrationAttempted ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center`} data-id="element-12278">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-12279">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-12280"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-12281"></path>
                        </svg>
                        Creating Account...
                      </> : 'Start Free'}
                  </button>

                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-12282">
                    You won't be charged during your 30-day free trial. You can
                    cancel anytime.
                  </p>

                  <p className="text-sm text-gray-500 text-center mt-4" data-id="element-12283">
                    Already have an account?{' '}
                    <Link to="/teacher-login" className="text-blue-600 hover:text-blue-700" data-id="element-12284">
                      Sign In
                    </Link>
                  </p>
                </div>
              </form>}

            {signupComplete && <div className="text-center py-8" data-id="element-12285">
                <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-12286">
                  <CheckCircleIcon className="h-12 w-12 text-green-500" data-id="element-12287" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-12288">
                  Account Created Successfully!
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-12289">
                  A verification email has been sent to your inbox. We're
                  redirecting you in {redirectCountdown} seconds.
                </p>
                <button onClick={() => navigate('/teacher-email-verification')} className="bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-6 rounded-lg transition-colors" data-id="element-12290">
                  Verify Your Email
                </button>
              </div>}
          </div>
        </div>
      </section>
    </div>;
};